import type Highcharts from "highcharts";
import React from "react";
import mergeDeep from "lodash.merge";
import type { Options } from "@ui/Highcharts";
import { useCommonOptions } from "../hook/useCommonOptions";
import t from "../../../../../i18n";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../../../api/useSetups";
import type { Project } from "../../../../../api/useProjects";
import { y_axis } from "@ui/Chart/Axis";
import type { Quantity } from "../../../../../api/useQuantities";

const useOptions = ({
  qoi,
  visibleSymptomHistogramChartId,
  series,
  analysis,
  setups,
  project,
  flagSelectedQuantities,
}: {
  visibleSymptomHistogramChartId: string;
  qoi: Quantity;
  series: Highcharts.SeriesColumnOptions[];
  analysis: ActiveAnalysis;
  setups: Setup[];
  project: Project;
  flagSelectedQuantities: boolean;
}): Options => {
  const commonOptions = useCommonOptions({
    visibleSymptomHistogramChartId,
    analysis,
    series,
    quantities: [qoi],
    project,
    setups,
    flagSelectedQuantities,
  });

  return React.useMemo(() => {
    const options = {
      chart: {
        marginLeft: 50,
        marginTop: 10,
        spacing: [0, 0, 5, 0],
      },
      plotOptions: {
        column: {
          groupPadding: 0,
          label: { enabled: false },
        },
      },
      xAxis: {
        visible: false, // hides the axis line & labels from the user
        categories: [qoi.name], // still provides data so the tooltip doesn't blow up
      },
      yAxis: {
        title: {
          text: t("diagnosis.charts.symptoms.axis.y", qoi.name, qoi.unit),
          style: {
            color: y_axis.title.color,
            font: y_axis.title.font,
          },
          offset: y_axis.title.offset,
          useHTML: true,
        },
        labels: {
          style: {
            color: y_axis.label.color,
            font: y_axis.label.font,
          },
          x: -1 * y_axis.label.offset,
          useHTML: true,
        },
      },
    };
    return mergeDeep(commonOptions, options);
  }, [qoi.name, qoi.unit, commonOptions]);
};

export default useOptions;
